import { Link } from "react-router-dom";
import { Shell } from "../components/Shell";

/**
 * Guía del módulo de Gestión Documental para admins y funcionarios
 * de archivo de la alcaldía. Cubre desde la activación por parte del
 * super_admin hasta el ciclo completo de un acto administrativo
 * (borrador → revisión → aprobado → publicado → archivado).
 *
 * Audiencia: admin_tenant y supervisores. Tono: claro, no técnico.
 */
export function DocsGestionDocumentalPage() {
  return (
    <Shell>
      <section className="page" style={{ maxWidth: 880 }}>
        <div className="eyebrow">
          <Link to="/docs">← Documentación</Link> · Gestión Documental
        </div>

        <h1 style={{ margin: "8px 0 12px", fontSize: 32 }}>
          Gestión Documental (Actos administrativos)
        </h1>

        <div className="callout">
          <strong>Tiempo de lectura:</strong> 15-20 min. Cubre desde la
          activación del módulo para una alcaldía hasta publicar el primer
          acto administrativo con firma electrónica.
        </div>

        {/* ---- 1. Qué es ---- */}
        <Section title="1 — Qué es Gestión Documental">
          <p>
            Es el módulo donde la alcaldía maneja sus{" "}
            <strong>actos administrativos</strong>: decretos, resoluciones,
            acuerdos, circulares, actas y contratos. Reemplaza la carpeta
            compartida + Excel con consecutivo manual que se usa hoy en la
            mayoría de las alcaldías.
          </p>
          <p>Lo que aporta sobre el flujo en papel:</p>
          <Ul>
            <li>
              <strong>Numeración consecutiva atómica</strong> por tipo y año
              (nunca dos decretos con el mismo número).
            </li>
            <li>
              <strong>Búsqueda full-text en español</strong> dentro del
              contenido del PDF (no solo título o número).
            </li>
            <li>
              <strong>Flujo de aprobación</strong>: borrador → revisión →
              aprobado → publicado → archivado, con rastro de auditoría.
            </li>
            <li>
              <strong>Versionado inmutable</strong>: cada vez que se reemplaza
              el archivo de un acto antes de publicar, queda guardada la
              versión anterior con motivo del cambio.
            </li>
            <li>
              <strong>Tabla de Retención Documental (TRD)</strong> por tipo,
              alineada al Archivo General de la Nación (Ley 594/2000).
            </li>
            <li>
              <strong>Firma electrónica PAdES</strong> automática al publicar,
              si el tipo lo requiere y la alcaldía tiene certificado PKCS#12
              cargado.
            </li>
            <li>
              <strong>Portal público de transparencia</strong> (Ley 1712/2014)
              con los actos publicados marcados como públicos.
            </li>
            <li>
              <strong>Asistente IA</strong> que redacta un borrador a partir
              de un prompt + plantilla y lo entrega como PDF listo para
              revisar.
            </li>
          </Ul>
        </Section>

        {/* ---- 2. Activación ---- */}
        <Section title="2 — Cómo se activa para una alcaldía">
          <p>
            Esto lo hace el <strong>super_admin de MiPueblo</strong>, no la
            alcaldía. Es un toggle por tenant.
          </p>
          <Ol>
            <li>
              En el panel super-admin: <Kbd>Alcaldías</Kbd> → elegir el tenant{" "}
              → tab <Kbd>Gestión Documental</Kbd>.
            </li>
            <li>
              Activar el submódulo <strong>Actos administrativos</strong>.
              (Existe también <strong>Correspondencia</strong> para radicación
              de oficios; es independiente y se activa aparte.)
            </li>
            <li>
              Una vez activo, dentro del tenant aparece el hub{" "}
              <code>/gd</code> en el sidebar de funcionarios y se habilitan
              los endpoints <code>/gd/*</code>.
            </li>
            <li>
              URL pública para los funcionarios:{" "}
              <code>[slug].mipuebloapp.com/gd/actos</code>.
            </li>
            <li>
              URL del portal ciudadano de transparencia:{" "}
              <code>[slug].mipuebloapp.com/documentos</code>.
            </li>
          </Ol>
          <div className="callout tip">
            Si el módulo no está activo, cualquier llamada a{" "}
            <code>/gd/*</code> devuelve <code>403</code> con mensaje{" "}
            <em>“Gestión Documental no está habilitada para esta alcaldía.”</em>
          </div>
        </Section>

        {/* ---- 3. Vista general ---- */}
        <Section title="3 — Vista general del módulo">
          <p>
            Al entrar a <code>/gd/actos</code>, el funcionario ve un listado
            paginado con buscador en la parte superior y filtros por:
          </p>
          <Ul>
            <li>
              <strong>Tipo</strong> (Decreto, Resolución, Acuerdo…)
            </li>
            <li>
              <strong>Estado</strong> (borrador, en revisión, aprobado,
              publicado, archivado)
            </li>
            <li>
              <strong>Año</strong> de expedición
            </li>
            <li>
              <strong>Texto libre</strong> — busca en título, descripción y
              contenido extraído del PDF
            </li>
          </Ul>
          <p>Botones principales arriba a la derecha:</p>
          <Table
            rows={[
              ["Nuevo documento", "Subir un PDF/DOCX ya redactado"],
              ["Nuevo con IA", "Generar borrador con el asistente y publicar"],
              ["Configuración", "Solo admin — tipos y plantillas"],
            ]}
          />
        </Section>

        {/* ---- 4. Crear manual ---- */}
        <Section title="4 — Crear un documento manualmente">
          <p>
            Usa esta vía cuando ya tienes el PDF (o DOCX) redactado por fuera
            — por ejemplo, lo preparó Jurídica en Word y solo necesitas
            subirlo al sistema.
          </p>
          <Ol>
            <li>
              Click en <Kbd>Nuevo documento</Kbd>.
            </li>
            <li>
              Llenar campos obligatorios:
              <Ul>
                <li>
                  <strong>Tipo</strong> — define el prefijo de numeración y
                  los campos por defecto (público sí/no, requiere firma sí/no)
                </li>
                <li>
                  <strong>Título</strong> — máx. 300 caracteres
                </li>
                <li>
                  <strong>Fecha de expedición</strong> — define el año del
                  consecutivo
                </li>
                <li>
                  <strong>Archivo</strong> — PDF, DOCX u otros, máx.{" "}
                  <strong>50 MB</strong>
                </li>
              </Ul>
            </li>
            <li>
              Opcionales: descripción, dependencia responsable, fecha de
              vencimiento, marcar público/privado.
            </li>
            <li>
              Guardar. El sistema:
              <Ul>
                <li>
                  Reserva el <strong>siguiente número consecutivo</strong>{" "}
                  atómicamente para ese tipo y año.
                </li>
                <li>
                  Compone el <strong>número completo</strong> con formato{" "}
                  <code>[SLUG]-[PREFIJO]-[AÑO]-[NÚMERO]</code> — ej:{" "}
                  <code>CAJ-DEC-2026-00042</code>.
                </li>
                <li>
                  Si es PDF, intenta <strong>extraer el texto</strong> con
                  pypdf para alimentar el buscador. Si el PDF es escaneado
                  (sin texto), encola un job de OCR en background.
                </li>
                <li>
                  Encola un job para generar el <strong>embedding</strong>{" "}
                  semántico (búsqueda por significado, además del FTS).
                </li>
                <li>
                  Deja el acto en estado <strong>borrador</strong>.
                </li>
              </Ul>
            </li>
          </Ol>
        </Section>

        {/* ---- 5. Crear con IA ---- */}
        <Section title="5 — Crear documento con IA">
          <p>
            Esta vía es para cuando partes desde cero y quieres que el LLM te
            redacte un borrador estructurado en lugar de empezar en Word
            mirando una página en blanco.
          </p>
          <Ol>
            <li>
              Click en <Kbd>Nuevo con IA</Kbd>.
            </li>
            <li>
              Configurar:
              <Ul>
                <li>
                  <strong>Tipo</strong> de acto (decreto, resolución…)
                </li>
                <li>
                  <strong>Plantilla</strong> (opcional) — esqueleto HTML con
                  encabezados, considerandos y bloque de firma. Si no eliges,
                  el LLM usa una estructura genérica del tipo.
                </li>
                <li>
                  <strong>Título</strong> y <strong>fecha de expedición</strong>
                </li>
                <li>
                  <strong>Prompt</strong> — descripción en lenguaje natural de
                  qué debe contener el acto (mín. 10 caracteres, máx. 4 000).
                  Sé específico: cita normas, nombres, montos, fechas.
                </li>
              </Ul>
            </li>
            <li>
              Click <Kbd>Generar</Kbd> → el LLM produce un{" "}
              <strong>borrador HTML</strong> en pocos segundos.
            </li>
            <li>
              Se muestra una <strong>vista previa</strong> con el HTML final
              (plantilla + merge tags como número, fecha, dependencia, +
              cuerpo generado). El número aún es{" "}
              <code>PREVIEW</code> — no se ha reservado todavía.
            </li>
            <li>
              Revisar y, si está bien, <Kbd>Crear documento</Kbd>. El sistema:
              <Ul>
                <li>Reserva el consecutivo real.</li>
                <li>Recompone el HTML con el número definitivo.</li>
                <li>
                  Renderiza HTML → PDF con WeasyPrint y lo guarda como el
                  archivo del acto.
                </li>
                <li>Deja el acto en estado <strong>borrador</strong>.</li>
              </Ul>
            </li>
          </Ol>
          <div className="callout warn">
            La IA redacta un <strong>borrador</strong>. Es responsabilidad del
            funcionario revisar contenido, citas normativas y nombres antes
            de aprobar. El sistema mantiene auditoría de quién aprobó y
            publicó.
          </div>
        </Section>

        {/* ---- 6. Tipos ---- */}
        <Section title="6 — Tipos documentales (catálogo)">
          <p>
            Cada acto pertenece a un <strong>tipo</strong>. Los tipos los
            configura el <strong>admin_tenant</strong> en{" "}
            <Kbd>Configuración → Tipos documentales</Kbd>. Campos de un tipo:
          </p>
          <Table
            head={["Campo", "Para qué sirve"]}
            rows={[
              [
                "Código",
                "Identificador corto (DEC, RES, ACU). Único por tenant.",
              ],
              ["Nombre", "Etiqueta visible (Decreto, Resolución…)"],
              [
                "Prefijo numeración",
                "Va dentro del número completo (CAJ-DEC-2026-00042)",
              ],
              [
                "Requiere firma",
                "Si está activo, al publicar se intenta firmar el PDF con el certificado del tenant",
              ],
              [
                "Público default",
                "Si los nuevos actos de este tipo salen al portal ciudadano por defecto",
              ],
              [
                "Retención (años)",
                "Cuántos años se conserva antes de disponer (TRD del AGN)",
              ],
              [
                "Disposición final",
                "Conservación total, eliminación, selección, microfilmación",
              ],
              [
                "Serie / Subserie",
                "Clasificación AGN (Ej: Serie ‘Actos Administrativos’, Subserie ‘Decretos’)",
              ],
              [
                "Activo",
                "Soft delete: si lo desactivas, no se pueden crear más actos de ese tipo, pero los existentes siguen vivos",
              ],
            ]}
          />
        </Section>

        {/* ---- 7. Estados y flujo ---- */}
        <Section title="7 — Estados y flujo de aprobación">
          <p>El ciclo de vida de un acto es lineal:</p>
          <Table
            head={["Estado", "Quién puede pasarlo", "Qué pasa"]}
            rows={[
              [
                "borrador",
                "—",
                "Estado inicial al crear. Editable. Se puede reemplazar el archivo.",
              ],
              [
                "en revisión",
                "Quien crea / supervisor",
                "Marcado para revisión. Aún editable. (Estado opcional — se puede pasar directo a aprobado).",
              ],
              [
                "aprobado",
                "admin_tenant",
                "El acto pasó el visto bueno. Ya no se puede reemplazar el archivo. Listo para publicar.",
              ],
              [
                "publicado",
                "admin_tenant",
                "Visible en portal ciudadano si publico=true. Si el tipo requiere firma, se firma con PAdES al publicar. Quedan registradas fecha y firmante.",
              ],
              [
                "archivado",
                "admin_tenant",
                "Se retira de circulación. vigente=false. No editable. Conservado para histórico.",
              ],
            ]}
          />
          <p style={{ marginTop: 14 }}>
            Reglas duras del workflow:
          </p>
          <Ul>
            <li>
              No se puede saltar de <code>borrador</code> directo a{" "}
              <code>publicado</code> — primero hay que aprobar.
            </li>
            <li>
              No se puede reemplazar el archivo de un acto{" "}
              <code>publicado</code> o <code>archivado</code>. Si te
              equivocaste, hay que <strong>crear un acto nuevo</strong> que lo
              reemplace.
            </li>
            <li>
              No se puede editar metadatos de un acto <code>archivado</code>.
            </li>
            <li>
              Todas las transiciones quedan en la{" "}
              <strong>auditoría</strong> (acción, actor, IP, timestamp).
            </li>
          </Ul>
        </Section>

        {/* ---- 8. Búsqueda ---- */}
        <Section title="8 — Búsqueda y filtros">
          <p>El buscador funciona en dos capas combinadas:</p>
          <Ol>
            <li>
              <strong>Full-text en español</strong> sobre título, descripción
              y contenido extraído del PDF, con ponderación (título pesa más
              que cuerpo).
            </li>
            <li>
              <strong>Búsqueda semántica por embedding</strong> (si la
              alcaldía tiene OpenAI configurado y el job de embedding corrió).
              Permite encontrar por significado aunque las palabras exactas no
              coincidan.
            </li>
          </Ol>
          <p>
            Si no hay embedding o falla, el sistema cae limpio a FTS — no se
            rompe.
          </p>
          <p style={{ marginTop: 10 }}>Para PDFs escaneados:</p>
          <Ul>
            <li>
              Al subir un PDF sin texto extraíble, se encola un job de{" "}
              <strong>OCR</strong> (necesita Redis + worker arq corriendo).
            </li>
            <li>
              Mientras el OCR corre, el acto está visible pero NO indexado en
              el FTS por contenido. Una vez termina, aparece en búsquedas.
            </li>
            <li>
              Si no hay worker arq disponible (dev sin Redis), el campo de
              contenido queda vacío. La búsqueda por título y descripción
              sigue funcionando.
            </li>
          </Ul>
        </Section>

        {/* ---- 9. Versiones ---- */}
        <Section title="9 — Archivo, versiones y descarga">
          <p>
            Cada acto tiene exactamente <strong>un archivo actual</strong>.
            Cuando se reemplaza (en estado borrador o revisión), el archivo
            anterior se guarda en el historial como una <strong>versión</strong>{" "}
            con motivo del cambio.
          </p>
          <Ul>
            <li>
              <strong>Reemplazar archivo</strong>: en el detalle del acto →{" "}
              <Kbd>Reemplazar archivo</Kbd>. Pide motivo del cambio. Se crea
              snapshot de la versión anterior, sube en uno el contador de
              versión y guarda el nuevo archivo.
            </li>
            <li>
              <strong>Versiones</strong>: en el detalle aparece la lista de
              versiones históricas, descargables individualmente.
            </li>
            <li>
              <strong>Firma PAdES</strong>: si el tipo requiere firma, al
              publicar el sistema firma el PDF y guarda{" "}
              <em>también</em> una versión sin firma en el historial (queda la
              traza del documento original).
            </li>
            <li>
              <strong>Descarga</strong>: cada descarga incrementa el contador{" "}
              <code>downloads</code>. Las descargas del portal público
              también cuentan.
            </li>
            <li>
              <strong>Hash SHA</strong>: cada archivo guarda su hash para
              integridad. Permite probar que el documento no fue alterado.
            </li>
          </Ul>
        </Section>

        {/* ---- 10. Configuración ---- */}
        <Section title="10 — Configuración (solo admin)">
          <p>
            Ruta: <code>/gd/configuracion</code>. Solo accesible para rol{" "}
            <code>admin_tenant</code>. Dos pestañas:
          </p>
          <Ol>
            <li>
              <strong>Tipos documentales</strong> — CRUD del catálogo
              explicado en la sección 6.
            </li>
            <li>
              <strong>Plantillas IA</strong> — esqueletos HTML que el LLM usa
              como base al generar borradores. Campos:
              <Ul>
                <li>
                  <strong>Tipo</strong> asociado (opcional — si lo dejas
                  vacío, la plantilla aplica a cualquier tipo)
                </li>
                <li>
                  <strong>Código</strong> y <strong>nombre</strong> visibles
                </li>
                <li>
                  <strong>Contenido HTML</strong> — usar{" "}
                  <code>{"{{numero_completo}}"}</code>,{" "}
                  <code>{"{{fecha_expedicion}}"}</code>,{" "}
                  <code>{"{{titulo}}"}</code>,{" "}
                  <code>{"{{dependencia}}"}</code>,{" "}
                  <code>{"{{contenido_principal}}"}</code> como merge tags
                </li>
                <li>
                  <strong>Instrucciones LLM</strong> — texto adicional para
                  guiar al modelo (tono, estructura, normativa a citar)
                </li>
              </Ul>
              Cada vez que cambias el HTML, sube la versión interna de la
              plantilla. El campo <code>uso_count</code> trackea cuántas veces
              se usó.
            </li>
          </Ol>
          <div className="callout tip">
            La <strong>firma electrónica</strong> (cargar el certificado
            PKCS#12 del alcalde) NO se configura aquí — la configura el
            super_admin desde el panel del tenant. Si el tipo dice{" "}
            <code>requiere_firma=true</code> pero no hay cert, el acto se
            publica sin firmar y queda anotado en auditoría.
          </div>
        </Section>

        {/* ---- 11. Permisos ---- */}
        <Section title="11 — Permisos por rol">
          <Table
            head={["Acción", "admin_tenant", "supervisor", "funcionario"]}
            rows={[
              ["Consultar / buscar / descargar", "Sí", "Sí", "Solo asignados"],
              ["Crear acto (manual o IA)", "Sí", "Sí", "No"],
              ["Editar metadatos / reemplazar archivo", "Sí", "Sí", "No"],
              ["Aprobar (borrador → aprobado)", "Sí", "No", "No"],
              ["Publicar (aprobado → publicado)", "Sí", "No", "No"],
              ["Archivar", "Sí", "No", "No"],
              ["Configurar tipos / plantillas", "Sí", "No", "No"],
            ]}
          />
          <p style={{ marginTop: 12 }}>
            La separación entre <em>crear/aprobar</em> y <em>publicar</em> es
            intencional: implementa el principio de los{" "}
            <strong>cuatro ojos</strong> (quien redacta no es quien publica),
            requisito común en control interno de las alcaldías.
          </p>
        </Section>

        {/* ---- 12. Integración ---- */}
        <Section title="12 — Integración con otros módulos">
          <Ul>
            <li>
              <strong>Portal ciudadano de transparencia</strong> —{" "}
              <code>[slug].mipuebloapp.com/documentos</code>. Solo muestra
              actos en estado <code>publicado</code> con{" "}
              <code>publico=true</code>. Cumple Ley 1712/2014 (Transparencia).
            </li>
            <li>
              <strong>Dependencias</strong> — cada acto puede asociarse a una
              dependencia responsable. El catálogo se administra desde el
              módulo de Equipo.
            </li>
            <li>
              <strong>PQRSDF</strong> — un acto publicado puede referenciarse
              como respuesta en una petición ciudadana (en roadmap).
            </li>
            <li>
              <strong>Correspondencia</strong> — submódulo independiente
              dentro de GD para radicación de oficios entrantes, salientes e
              internos. Tiene consecutivos separados (<code>CORR/OFI/MEM</code>),
              sello digital automático, acuse al remitente por WhatsApp + email
              y asignación por categoría bajo Acuerdo AGN 060/2001.
            </li>
            <li>
              <strong>Auditoría</strong> — todas las acciones (crear, editar,
              aprobar, publicar, archivar, reemplazar archivo, configurar
              tipos) quedan en la tabla central de auditoría con actor, IP y
              cambios.
            </li>
          </Ul>
        </Section>

        {/* ---- 13. Buenas prácticas ---- */}
        <Section title="13 — Buenas prácticas">
          <Checklist
            items={[
              <>
                Configurar los <strong>tipos documentales</strong> con su
                serie/subserie AGN <em>antes</em> de empezar a cargar actos —
                cambiar el prefijo después no renumera lo ya creado.
              </>,
              <>
                Definir <strong>retención en años</strong> y disposición final
                de cada tipo siguiendo la TRD aprobada del municipio (Comité
                Interno de Archivo).
              </>,
              <>
                Para actos firmados por el alcalde, configurar el{" "}
                <strong>certificado PKCS#12</strong> antes de marcar el tipo
                como <code>requiere_firma</code>.
              </>,
              <>
                Usar <strong>plantillas IA</strong> con encabezados oficiales,
                escudo del municipio y bloque de firma — así todos los actos
                generados con IA salen homogéneos.
              </>,
              <>
                Llenar el campo <strong>descripción</strong> con un resumen de
                3-5 líneas — el buscador la usa con peso alto y a los
                ciudadanos les ayuda a entender de qué trata el acto.
              </>,
              <>
                Subir PDFs con <strong>texto seleccionable</strong> siempre
                que sea posible. Para escaneados, esperar a que el OCR termine
                antes de publicar (revisar que aparezca en búsquedas por
                contenido).
              </>,
              <>
                Antes de aprobar un borrador generado por IA, leerlo
                <strong> entero</strong>. El modelo puede inventar
                referencias normativas (alucinar artículos) — verificar con
                fuente oficial.
              </>,
              <>
                Capacitar a los funcionarios en la diferencia entre{" "}
                <strong>archivar</strong> (retira de circulación, queda
                histórico) y <strong>eliminar</strong> (no existe en GD —
                solo se desactiva el tipo).
              </>,
              <>
                Mantener un <strong>respaldo externo</strong> de los archivos
                físicos: GD guarda los binarios en disco bajo{" "}
                <code>data/tenants/[slug]/gd/</code> — asegurar que esté en el
                plan de backups del servidor.
              </>,
            ]}
          />
        </Section>
      </section>
    </Shell>
  );
}

/* ============ helpers de presentación ============ */

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section
      style={{
        marginTop: 32,
        paddingTop: 24,
        borderTop: "1px solid var(--hairline)",
      }}
    >
      <h2 style={{ fontSize: 20, marginTop: 0, marginBottom: 14 }}>{title}</h2>
      <div style={{ lineHeight: 1.6, color: "var(--ink-2)", fontSize: 14.5 }}>
        {children}
      </div>
    </section>
  );
}

function Ol({ children }: { children: React.ReactNode }) {
  return (
    <ol style={{ paddingLeft: 22, margin: "8px 0", display: "grid", gap: 6 }}>
      {children}
    </ol>
  );
}

function Ul({ children }: { children: React.ReactNode }) {
  return (
    <ul style={{ paddingLeft: 22, margin: "8px 0", display: "grid", gap: 6 }}>
      {children}
    </ul>
  );
}

function Checklist({ items }: { items: React.ReactNode[] }) {
  return (
    <ul style={{ listStyle: "none", padding: 0, margin: "8px 0", display: "grid", gap: 8 }}>
      {items.map((it, i) => (
        <li key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
          <span
            aria-hidden
            style={{
              flexShrink: 0,
              width: 16,
              height: 16,
              border: "1.5px solid var(--ink-3)",
              borderRadius: 4,
              marginTop: 3,
            }}
          />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        background: "var(--bone-warm)",
        border: "1px solid var(--hairline-strong)",
        borderRadius: 4,
        padding: "1px 6px",
        fontFamily: "var(--font-mono)",
        fontSize: 12.5,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

function ExtLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noreferrer">
      {children} ↗
    </a>
  );
}

function Table({
  head,
  rows,
}: {
  head?: string[];
  rows: (string | React.ReactNode)[][];
}) {
  return (
    <div
      style={{
        marginTop: 10,
        border: "1px solid var(--hairline)",
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: 13.5,
        }}
      >
        {head && (
          <thead>
            <tr style={{ background: "var(--bone-warm)" }}>
              {head.map((h, i) => (
                <th
                  key={i}
                  style={{
                    textAlign: "left",
                    padding: "8px 12px",
                    fontFamily: "var(--font-mono)",
                    fontSize: 10.5,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "var(--ink-3)",
                    fontWeight: 500,
                    borderBottom: "1px solid var(--hairline)",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              style={{
                borderTop: i === 0 && !head ? 0 : "1px solid var(--hairline)",
              }}
            >
              {row.map((cell, j) => (
                <td
                  key={j}
                  style={{
                    padding: "10px 12px",
                    verticalAlign: "top",
                    fontWeight: j === 0 ? 600 : 400,
                  }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Mantiene ExtLink exportable a futuro si añadimos enlaces externos.
void ExtLink;
